/**
 * LE SOLDE DE SMS DE L'ENTREPRISE, RELU PAR LA PAGE « OPTION AVANCÉ ».
 * ============================================================================
 *
 * INTENTION : la page dit le solde tel que le service le tient au moment où
 * elle s'ouvre, et l'historique des mouvements à qui y a droit. Elle relit
 * `GET /alarmes-v2/sms` à chaque entrée (l'offre lue à l'ouverture de la
 * session peut dater) ; `mouvements` n'y est servi qu'à un propriétaire ou
 * un administrateur (api-metier `alarmesv2.sms.js` `lireSms`).
 *
 * Jamais de faux calme :
 *  • une réponse illisible (pas d'objet, `module_souscrit` non booléen, un
 *    solde ni nombre ni `null`, une raison inconnue) est « indisponible »,
 *    jamais un solde à 0 ni « rien à signaler » ;
 *  • un solde `null` reste `null` : l'écran dit « solde non disponible » ;
 *  • `mouvements` absent reste absent (non servi), jamais une liste vide.
 *
 * Tenu par session (personne et entreprise, `cleDeSession`) : une fin de
 * lecture qui n'appartient plus à la session courante est jetée, en succès
 * comme en échec, sans toucher à l'état.
 */
import { defineStore } from "pinia"
import { ref } from "vue"
import { useAuthStore } from "@core/stores/auth.store"
import { cleDeSession } from "@modules/alarmes-v2/stores/ouverture.store"
import { serviceAlarmesV2 } from "@modules/alarmes-v2/services/alarmes-v2.service"
import type {
    AlarmesV2MouvementSms,
    AlarmesV2Sms,
} from "@modules/alarmes-v2/types/alarmes-v2"

/** Où en est la lecture du solde. */
export type StatutSms = "attente" | "chargement" | "pret" | "indisponible"

/** Les raisons qu'un solde indisponible peut servir (`projeterSms`). */
const RAISONS = [null, "module_sms_non_souscrit", "solde_sms_epuise"]

/** Vrai pour un objet non nul qui n'est pas un tableau. */
function estObjet(v: unknown): v is Record<string, unknown> {
    return v !== null && typeof v === "object" && !Array.isArray(v)
}

/** Un nombre lu, ou `null` : jamais un 0 supposé. */
const nombre = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) ? v : null

/** Une chaîne lue, ou `null`. */
const chaine = (v: unknown): string | null =>
    typeof v === "string" && v !== "" ? v : null

/**
 * Le solde servi, ou `null` s'il est illisible.
 * @param brut - la réponse de la route
 */
export function lireLeSolde(brut: unknown): AlarmesV2Sms | null {
    if (!estObjet(brut)) return null
    if (typeof brut.module_souscrit !== "boolean") return null
    const solde = brut.solde_segments
    if (solde !== null && nombre(solde) === null) return null
    if (!RAISONS.includes(brut.raison_indisponible as string | null))
        return null
    const lu: AlarmesV2Sms = {
        module_souscrit: brut.module_souscrit,
        solde_segments: solde as number | null,
        raison_indisponible:
            brut.raison_indisponible as AlarmesV2Sms["raison_indisponible"],
    }
    if (Array.isArray(brut.mouvements)) {
        lu.mouvements = brut.mouvements.map(
            (m): AlarmesV2MouvementSms => ({
                sens: estObjet(m) ? chaine(m.sens) : null,
                segments: estObjet(m) ? nombre(m.segments) : null,
                solde_apres: estObjet(m) ? nombre(m.solde_apres) : null,
                le: estObjet(m) ? chaine(m.le) : null,
            })
        )
    }
    return lu
}

export const useSmsStore = defineStore("alarmesV2Sms", () => {
    /** Où en est la lecture. */
    const statut = ref<StatutSms>("attente")

    /** Le solde lu, `null` tant qu'il n'est pas lu ou s'il est indisponible. */
    const sms = ref<AlarmesV2Sms | null>(null)

    /** La clé de session de l'état montré. */
    const cleLue = ref("")

    let enVol: Promise<void> | null = null
    let enVolPour = ""

    /**
     * Relit le solde pour la session courante. Ne rejette jamais. Deux appels
     * simultanés pour la même session partagent une requête.
     */
    async function charger(): Promise<void> {
        const cle = cleDeSession()
        if (!useAuthStore().companyId) {
            reinitialiser()
            return
        }
        if (enVol && enVolPour === cle) return enVol
        if (cleLue.value !== cle) sms.value = null
        cleLue.value = cle
        statut.value = "chargement"
        enVolPour = cle
        const lecture = (async () => {
            let lu: AlarmesV2Sms | null = null
            try {
                lu = lireLeSolde(await serviceAlarmesV2.alarmesV2LireSms())
            } catch {
                lu = null
            }
            if (cleDeSession() !== cle) return
            sms.value = lu
            statut.value = lu ? "pret" : "indisponible"
        })()
        enVol = lecture
        try {
            await lecture
        } finally {
            if (enVol === lecture) {
                enVol = null
                enVolPour = ""
            }
        }
    }

    /** Remet le store à son état de départ. */
    function reinitialiser(): void {
        statut.value = "attente"
        sms.value = null
        cleLue.value = ""
        enVol = null
        enVolPour = ""
    }

    return { statut, sms, cleLue, charger, reinitialiser }
})
